<template>
    <div class="ReportsPane">
        <div class="container-fluid container-fill">
            <div class="row">
                <h2>Foster Reporting</h2>
            </div>
            <div class="row">
                <div class="col-md-4">
                    <h4>Child Reports</h4>
                    <ReportDownloader report-label="Program Census Report"
                                      endpoint="Core/Reports/ProgramReports/SubProgramCensusReport"
                                      :parameter-schema="ParamsSubProgramStartEnd" />

                    <ReportDownloader report-label="Sample Child Charts Audit"
                                      endpoint="Foster/ChartQA/AuditClientCharts"
                                      :parameter-schema="ParamsSubProgramStartEnd"
                                      report-description="This report shows a sample of what's possible with the new Chart QA feature.  It allows you to audit all of your program's case files at the same time, based on rules specific to your program.  Since this report scans through all of your programs files, it can take several minutes to run." />

                    <ReportDownloader report-label="Caseload Report"
                                      endpoint="Foster/Reports/ProgramReports/GetCaseManagerCaseloadReport"
                                      :parameter-schema="ParamsSubProgramCaseloadOptions"
                                      report-description="Provides a list of children, grouped by Case Manager, with caseload counts (Excel)" />

                    <ReportDownloader report-label="Child Roster"
                                      endpoint="Core/Reports/ProgramReports/GetClientRoster"
                                      :parameter-schema="ParamsSubProgramMultiSelectStartEnd"
                                      report-description="Lists all clients in a program for at least one day during the specified time range." />

                    <ReportDownloader report-label="Child Accounting Roster"
                                      endpoint="Foster/Reports/ProgramReports/ChildAccountingRoster"
                                      :parameter-schema="ParamsSubProgramMonthYearMode"
                                      report-description="Provides one line item for each family/program/level combination in the given month and year.  Used for invoicing purposes." />
                    <ReportDownloader report-label="PNW CPA Billing Data"
                                      endpoint="Foster/Reports/ProgramReports/PNWCPABillingRoster"
                                      :parameter-schema="ParamsSubProgramMonthYear"
                                      report-description="Generates Data to Copy/Paste into CPA Billing Tool for PNW Foster" />
                    <ReportDownloader report-label="Child Birthdays"
                                      endpoint="Core/Reports/ProgramReports/GetBirthdays"
                                      :parameter-schema="ParamsClientBirthdayOptions"
                                      report-description="Lists the birthdays of current children, with options to specify the level of privacy on the generated list." />
                    <ReportDownloader report-label="Child Outcomes Summary"
                                      endpoint="Foster/Reports/ProgramReports/ClientOutcomes"
                                      :parameter-schema="ParamsSubProgramStartEnd"
                                      report-description="Lists the children who have terminated in the range specified, indicating their termination reasons, last placement, and adoptive family if applicable" />

                </div>
                <div class="col-md-4">
                    <h4>Family Reports</h4>
                    <ReportDownloader report-label="Family Roster"
                                      endpoint="Foster/Reports/ProgramReports/GetFamilyRoster"
                                      :parameter-schema="ParamsSubProgramStatus" />

                    <ReportDownloader report-label="Generate Parent Expiration Letters"
                                      endpoint="Foster/Reports/ProgramReports/GetParentExpirationLetters"
                                      :parameter-schema="ParamsSubProgram"
                                      permission-requirement="ReportAccess_PNWSpecialReports"
                                      report-description="Generates a PDF letter for each active Foster Family, listing the upcoming Parent expirations, children nearing their 16y or 18y backgroundcheck requirement, and upcoming rabies vaccinations for pets in the home." />
                    <ReportDownloader report-label="Foster Family Audit Report"
                                      endpoint="Foster/Reports/ProgramReports/FosterFamilyAuditReport"
                                      :parameter-schema="ParamsSubProgramStartEnd"
                                      permission-requirement="ReportAccess_PNWSpecialReports"
                                      report-description="Provides a report that checks the following for past/upcoming due during the period selected:  Parent Expirations, Children in Home background checks, and Pet vaccinations.  This will display in tabular format the same information that appears in the Parent Letter report." />
                    <ReportDownloader report-label="Family Member Flu Shots"
                                      endpoint="Foster/Reports/ProgramReports/GetFamilyMemberFluShotReport"
                                      :parameter-schema="ParamsSubProgramMultiSelectStartEnd"
                                      report-description="Lists the last flu shot date for all Foster Parents and Foster Family Members.  Any flu shot that is missing or occurs before the specified Cutoff Date will be hilighted in yellow." />
                    <ReportDownloader report-label="Foster Family Data Sheet"
                                      endpoint="Foster/Reports/ProgramReports/GetFamilyDatasheet"
                                      :parameter-schema="ParamsSubProgramMultiSelectStartEnd" />
                    <ReportDownloader report-label="Foster Family Status Changes"
                                      endpoint="Foster/Reports/ProgramReports/FosterFamilyStatusChanges"
                                      :parameter-schema="ParamsSubProgramMultiSelectStartEnd" />
                    <ReportDownloader report-label="Family Applicant Status Report"
                                      endpoint="Foster/Reports/ProgramReports/ApplicantStatusReport"
                                      :parameter-schema="ParamsSubProgramMultiSelect"
                                      report-description="Provides a list of all current Applicant Families, with info on last activity, and activities completed." />
                    <ReportDownloader report-label="Family Status Dates Summary (DRAFT)"
                                      endpoint="Foster/Reports/ProgramReports/FamilyStatusDates"
                                      :parameter-schema="ParamsStartEnd"
                                      report-description="Provides a list for all foster programs with dates on which approval/status changes occurred in the specified time range." />
                    <ReportDownloader report-label="Family Status Activity Roster"
                                      endpoint="Foster/Reports/ProgramReports/FamilyStatusActivityRoster"
                                      :parameter-schema="ParamsSubProgramMultiSelectStartEnd"
                                      report-description="Breakdown of the major milestones by each Foster Family in the specified region and time period." />
                    <ReportDownloader report-label="Annual Family Approval Summary (DRAFT)"
                                      endpoint="Foster/Reports/ProgramReports/AnnualApprovalSummary"
                                      :parameter-schema="ParamsSubProgramYear"
                                      report-description="WARNING- THIS REPORT IS STILL BEING TESTED FOR ACCURACY.  USE AT YOUR OWN PERIL  ;)  Shows summaries of approval/licensure activity over a calendar year" />
                    <ReportDownloader report-label="Approval Pipeline"
                                      endpoint="Foster/Reports/ProgramReports/ApprovalPipeline"
                                      :parameter-schema="ParamsSubProgram"
                                      report-description="Generates a report showing families with current Inquiry Status, current Applicant Status, and who have closed applications in the last 90 days." />
                    <ReportDownloader report-label="Approval Status Timeline"
                                      endpoint="Foster/Reports/ProgramReports/FamilyStatusTimeline"
                                      :parameter-schema="ParamsSubProgramStartEnd"
                                      report-description="Shows timeline for each family's approval, along with the ultimate outcome" />
                    <ReportDownloader report-label="Active Families Net Change Report"
                                      endpoint="Foster/Reports/ProgramReports/FamilyNetChangeReport"
                                      :parameter-schema="ParamsStartEnd"
                                      report-description="Lists all Olive Crest Foster Programs, and displays information on the number of families who experienced specific statuses in the time range selected." />
                </div>
                <div class="col-md-4">
                    <h4>Financial Reports</h4>
                    <ReportDownloader report-label="Census Verification"
                                      report-description="Downloads a list of activity for the period indicated.  An identical report can be emailed to the program's distribution list for approval/rejection."
                                      endpoint="Core/CensusApprovals/CreateCensusApproval"
                                      :parameter-schema="ParamsSubProgramStartEndReportType" />
                    <ReportDownloader report-label="Census Financials"
                                      report-description="Generates Revenue/Expense transactions for all billable activity in the specified month and year."
                                      endpoint="Core/CensusApprovals/TransactionsReport"
                                      permission-requirement="Reports.CensusFinancials"
                                      :parameter-schema="ParamsSubProgramMonthYear" />


                </div>
                <div class="col-md-4">
                    <h4>Other Reports</h4>
                    <ReportDownloader report-label="Respite Report"
                                      endpoint="Foster/Reports/ProgramReports/RespiteReport"
                                      :parameter-schema="ParamsSubProgramStartEnd"
                                      report-description="Lists all of the Respites that occurred in the selected date range." />
                    <ReportDownloader report-label="PNW Quarterly WACF Report"
                                      endpoint="Foster/Reports/PNWCPAQualityReport"
                                      :parameter-schema="ParamsStartEnd"
                                      permission-requirement="ReportAccess_PNWSpecialReports"
                                      icon="faFileExcel"
                                      report-description="Generates lists of data for completing the WACF CPA Quarterly report.  NOTE: This report will take a long time to run" />
                    <ReportDownloader report-label="PNW CPA Smartsheet"
                                      endpoint="Foster/Reports/ProgramReports/PNWCPASmartSheet"
                                      :parameter-schema="ParamsSubProgramStartEnd"
                                      permission-requirement="ReportAccess_PNWSpecialReports"
                                      icon="faFileExcel"
                                      report-description="Generates data for PNW CPA Smartsheet" />
                    <ReportDownloader report-label="PNW Community Connections"
                                      endpoint="Foster/Reports/ProgramReports/PNWCommunityContacts"
                                      :parameter-schema="ParamsSubProgramStartEnd"
                                      permission-requirement="ReportAccess_PNWSpecialReports"
                                      icon="faFileExcel"
                                      report-description="Provides a list of all clients who were: enrolled at both the beginning and end of the target range, and who were 6+ years old at the end of the target range.  Report will show if these clients had a contact record that indicated a Community Connection was present, and provides a total at the end of the report." />
                    <ReportDownloader report-label="Average Daily Caseloads"
                                      endpoint="Core/Reports/ProgramReports/AverageDailyCaseloads"
                                      :parameter-schema="ParamsSubProgramStartEnd"
                                      report-description="Provides a list of Average Daily Caseloads per employee in the given time range.  Also provides Average Daily Weighted Caseloads, and a breakdown by client Rate/Level." />
                    <ReportDownloader report-label="Daily Case Manager Caseload Itemization"
                                      endpoint="Foster/Reports/ProgramReports/DailyCaseloads"
                                      :parameter-schema="ParamsSubProgramStartEnd"
                                      permission-requirement="Administrator"
                                      report-description="Lists each Case Manager's caseload size (and weighted caseload size) for each day in the time period selected." />
                    <ReportDownloader report-label="Discharge Outcomes By Employee"
                                      permission-requirement="Foster.Employees.Productivity"
                                      endpoint="Core/Reports/ProgramReports/DischargeOutcomesByEmployee"
                                      :parameter-schema="ParamsSubProgramStartEnd"
                                      report-description="Provides a list that shows average length of stay by Caseload Assignment" />
                    <ReportDownloader report-label="Care Community Report"
                                      endpoint="Foster/Reports/ProgramReports/CareCommunityReports"
                                      :parameter-schema="ParamsSubProgramRegion"
                                      report-description="Provides a list of care communities and families."
                                      permission-requirement="Foster.CareCommunity" />

                </div>
                <div class="col-md-4">
                    <h4>Caregiver Support Reports</h4>
                    <ReportDownloader report-label="PNW Caregiver Support Billing Data"
                                      endpoint="Foster/Reports/ProgramReports/PNWCaregiverSupportBillingRoster"
                                      :parameter-schema="ParamsSubProgramMonthYear"
                                      report-description="Generates Data to Copy/Paste into Caregiver Support Billing Tool for PNW Caregiver Support" />
                    <ReportDownloader permission-requirement="ImportPNWCaregiverSupportClients"
                                      report-label="PNW Caregiver Support Client List Bulk Update"
                                      endpoint="Foster/Reports/ProgramReports/ImportPNWCaregiverSupportClients"
                                      :parameter-schema="ParamsPNWCaregiverSupportImportClients"
                                      method="POST"
                                      report-description="Imports clients into the selected PNW Caregiver Support program.  WARNING: This is a bulk upload operation that is difficult to undo.  Please check list and selected program carefully." />
                    <ReportDownloader permission-requirement="ImportPNWCaregiverSupportClients"
                                      report-label="PNW Caregiver Support Program Metrics"
                                      endpoint="Foster/Reports/ProgramReports/PNWCaregiverSupportMetrics"
                                      :parameter-schema="ParamsSubProgramStartEnd"
                                      method="GET"
                                      report-description="Lists metrics used for monthly report and billing." />
                    <ReportDownloader report-label="PNW Caregiver Support Client Phases"
                                      endpoint="Foster/Reports/ProgramReports/ClientPhaseHistory"
                                      :parameter-schema="ParamsSubProgramStartEnd"
                                      method="GET"
                                      report-description="Lists all the client phases (Caregiver Support Statuses) for the time period selected" />
                    <ReportDownloader permission-requirement="Administrator"
                                      report-label="PNW Caregiver Support Client Timeline"
                                      endpoint="Foster/Reports/ProgramReports/PNWCaregiverSupportClientTimeline"
                                      :parameter-schema="ParamsSubProgramStartEnd"
                                      method="GET"
                                      report-description="Lists billable days for clients for the time range selected" />

                </div>


            </div>
            <!--<div class="row">
                <div class="col">
                    <CensusDashboard />
                </div>
                
            </div>-->
        </div>
    </div>
</template>

<script>
    //import icons
    import { library } from '@fortawesome/fontawesome-svg-core';
    import { faPlus } from '@fortawesome/free-solid-svg-icons';
    library.add(faPlus);
    import { faList } from '@fortawesome/free-solid-svg-icons';
    library.add(faList);
    import { faChild } from '@fortawesome/free-solid-svg-icons';
    library.add(faChild);
    //import components
    import ReportDownloader from '../../Core/UI/ReportDownloader.vue';
    export default {
        props: [],
        data: function () {
            return {
                ParamsSubProgram: [
                    {
                        Label: "Program:",
                        Name: "Id",
                        Type: "Select",
                        Required: true,
                        Options: []
                    }
                ],
                ParamsSubProgram: [
                    {
                        Label: "Program:",
                        Name: "SubProgramID",
                        Type: "Select",
                        Required: true,
                        Options: []
                    }
                ],
                ParamsSubProgramMultiSelect: [
                    {
                        Label: "Program:",
                        Name: "Id",
                        Type: "MultiSelect",
                        Required: true,
                        Options: []
                    }
                ],
                ParamsSubProgramCaseloadOptions: [
                    {
                        Label: "Program:",
                        Name: "Id",
                        Type: "Select",
                        Required: true,
                        Options: []
                    },
                    {
                        Label: "Columns to Show:",
                        Name: "col_",
                        Type: "CheckBoxPrefixed",
                        Required: false,
                        Options: [
                            {
                                text: "Child Name",
                                nameSuffix: "name",
                                checked: true
                            },
                            {
                                text: "DOB",
                                nameSuffix: "dob",
                                checked: true
                            },
                            {
                                text: "Level of Care (Rate)",
                                nameSuffix: "loc",
                                checked: true
                            },
                            {
                                text: "Olive Crest Original Placement Date (OPD)",
                                nameSuffix: "opd",
                                checked: false
                            },
                            {
                                text: "Date of Current Placement",
                                nameSuffix: "dateofcurrentplacement",
                                checked: true
                            },
                            {
                                text: "Social Worker",
                                nameSuffix: "socialworker",
                                checked: false
                            },
                            {
                                text: "Foster Family",
                                nameSuffix: "family",
                                checked: true
                            },
                            {
                                text: "Location (city)",
                                nameSuffix: "location",
                                checked: true
                            },
                            {
                                text: "Placing Agency",
                                nameSuffix: "agency",
                                checked: true
                            },
                            {
                                text: "Physician",
                                nameSuffix: "physician",
                                checked: false
                            }
                        ]
                    }
                ],
                ParamsClientBirthdayOptions: [
                    {
                        Label: "Program:",
                        Name: "SubProgramId",
                        Type: "Select",
                        Required: true,
                        Options: []
                    },
                    {
                        Label: "Columns to Show:",
                        Type: "CheckBoxBool",
                        Required: false,
                        Options: [
                            {
                                text: "Include Last Name",
                                name: "IncludeLastName",
                                checked: true
                            },
                            {
                                text: "Include Date Of Birth",
                                name: "IncludeDateOfBirth",
                                checked: true
                            },
                            {
                                text: "Include Year Of Birth",
                                name: "IncludeYearOfBirth",
                                checked: true
                            },
                            {
                                text: "Include Month",
                                name: "IncludeDayOfMonth",
                                checked: true
                            },
                            {
                                text: "Include Day",
                                name: "IncludeDay",
                                checked: true
                            },
                            {
                                text: "Include Age",
                                name: "IncludeAge",
                                checked: true
                            },
                            {
                                text: "Include Gender",
                                name: "IncludeGender",
                                checked: true
                            }
                        ]
                    }
                ],
                ParamsSubProgramStatus: [
                    {
                        Label: "Program:",
                        Name: "SubProgramId",
                        Type: "Select",
                        Required: true,
                        Options: []
                    },
                    {
                        Label: "Family Status:",
                        Name: "Status",
                        Type: "Select",
                        Required: true,
                        Options: [
                            {
                                text: "All",
                                value: "All"
                            },
                            {
                                text: "Inquiries",
                                value: "Inquiries"
                            },
                            {
                                text: "All Applicants",
                                value: "All Applicants"
                            },
                            {
                                text: "Active",
                                value: "Active"
                            },
                            {
                                text: "Temporary Inactive",
                                value: "Inactive"
                            },
                            {
                                text: "Application Closed",
                                value: "Application Closed"
                            },
                            {
                                text: "Approval/License Rescinded/Forfeited",
                                value: "Approval/License Rescinded/Forfeited"
                            },
                        ]
                    },
                ],
                ParamsStartEnd: [
                    {
                        Label: "Start Date:",
                        Name: "StartDate",
                        Type: "Date",
                        Required: true
                    },
                    {
                        Label: "End Date:",
                        Name: "EndDate",
                        Type: "Date",
                        Required: true
                    }
                ],
                ParamsSubProgramStartEnd: [
                    {
                        Label: "Program:",
                        Name: "SubProgramID",
                        Type: "Select",
                        Required: true,
                        Options: []
                    },
                    {
                        Label: "Start Date:",
                        Name: "StartDate",
                        Type: "Date",
                        Required: true
                    },
                    {
                        Label: "End Date:",
                        Name: "EndDate",
                        Type: "Date",
                        Required: true
                    }
                ],
                ParamsSubProgramStartEndReportType: [
                    {
                        Label: "Program:",
                        Name: "SubProgramID",
                        Type: "Select",
                        Required: true,
                        Options: []
                    },
                    {
                        Label: "Start Date:",
                        Name: "StartDate",
                        Type: "Date",
                        Required: true
                    },
                    {
                        Label: "End Date:",
                        Name: "EndDate",
                        Type: "Date",
                        Required: true
                    },
                    {
                        Label: "Report Type:",
                        Name: "ReportType",
                        Type: "Select",
                        Required: true,
                        Options: [
                            {
                                text: "View Report Only",
                                value: "Download Only"
                            },
                            {
                                text: "Generate New Approval Email",
                                value: "Generate Approval Email"
                            },
                        ]
                    },
                ],
                ParamsSubProgramYear: [
                    {
                        Label: "Program:",
                        Name: "SubProgramID",
                        Type: "Select",
                        Required: true,
                        Options: []
                    },
                    {
                        Label: "Year:",
                        Name: "Year",
                        Type: "Number",
                        Required: true
                    }
                ],
                ParamsSubProgramMultiSelectStartEnd: [
                    {
                        Label: "Program:",
                        Name: "SubProgramID",
                        Type: "MultiSelect",
                        Required: true,
                        Options: []
                    },
                    {
                        Label: "Start Date:",
                        Name: "StartDate",
                        Type: "Date",
                        Required: true
                    },
                    {
                        Label: "End Date:",
                        Name: "EndDate",
                        Type: "Date",
                        Required: true
                    }
                ],
                ParamsSubProgramRegion: [
                    {
                        Label: "Program:",
                        Name: "SubProgramID",
                        Type: "Select",
                        Required: true,
                        Options: []
                    }
                ],
                ParamsSubProgramMonthYear: [
                    {
                        Label: "Program:",
                        Name: "SubProgramID",
                        Type: "Select",
                        Required: true,
                        Options: []
                    },
                    {
                        Label: "Month (1-12):",
                        Name: "Month",
                        Type: "Number",
                        Required: true
                    },
                    {
                        Label: "Year (4 digits):",
                        Name: "Year",
                        Type: "Number",
                        Required: true
                    },
                    {
                        Label: "Report Type:",
                        Name: "ReportType",
                        Type: "Select",
                        Required: true,
                        Options: [
                            {
                                text: "View Report Only",
                                value: "Download Only"
                            },
                            {
                                text: "Generate New Approval Email",
                                value: "Generate Approval Email"
                            },
                        ]
                    },
                ],
                ParamsSubProgramMonthYearMode: [
                    {
                        Label: "Program:",
                        Name: "SubProgramID",
                        Type: "Select",
                        Required: true,
                        Options: []
                    },
                    {
                        Label: "Month (1-12):",
                        Name: "Month",
                        Type: "Number",
                        Required: true
                    },
                    {
                        Label: "Year (4 digits):",
                        Name: "Year",
                        Type: "Number",
                        Required: true
                    },
                    {
                        Label: "Report Mode:",
                        Name: "ReportMode",
                        Type: "Select",
                        Required: true,
                        Options: [
                            {
                                text: "Trial (runs report only)",
                                value: "Trial"
                            },
                            {
                                text: "Preserve (saves copy for future comparison)",
                                value: "Preserve"
                            }
                        ]
                    },
                ],
                ParamsSubProgramStartEndProgramOptional: [
                    {
                        Label: "Program:",
                        Name: "SubProgramID",
                        Type: "Select",
                        Required: false,
                        Options: []
                    },
                    {
                        Label: "Start Date:",
                        Name: "StartDate",
                        Type: "Date",
                        Required: true
                    },
                    {
                        Label: "End Date:",
                        Name: "EndDate",
                        Type: "Date",
                        Required: true
                    }
                ],
                ParamsSubProgramEffectiveDate: [
                    {
                        Label: "Program:",
                        Name: "SubProgramID",
                        Type: "Select",
                        Required: true,
                        Options: []
                    },
                    {
                        Label: "Cutoff Date:",
                        Name: "EffectiveDate",
                        Type: "Date",
                        Required: true
                    }
                ],
                ParamsPNWCaregiverSupportImportClients: [
                    {
                        Label: "Program:",
                        Name: "SubProgramID",
                        Type: "Select",
                        Required: true,
                        Options: []
                    },
                    {
                        Label: "Date of Report Being Imported:",
                        Name: "ReportDate",
                        Type: "Date",
                        Required: true
                    },
                    {
                        Label: "Youth List to Import:",
                        Name: "Report",
                        Type: "FileUpload",
                        Accept: ".xlsx",
                        Required: true
                    },
                    {
                        Label: "Run Mode:",
                        Name: "RunMode",
                        Type: "Select",
                        Required: true,
                        Options: [
                            {
                                text: "Trial (see what happens- no records modifed)",
                                value: "Trial"
                            },
                            {
                                text: "Modify (will add new clients when needed)",
                                value: "Modify"
                            }
                        ]
                    },
                ]
            };
        },
        methods: {

        },
        computed: {
            SubProgramOptions: function () {
                return this.$store.getters["User.PrivateProfile.SubPrograms.GetSubProgramOptions"]();
            },
            SubProgramRegionOptions: function () {
                return this.$store.getters["User.PrivateProfile.SubPrograms.GetSubProgramRegionOptions"]();
            },
        },
        components: {
            ReportDownloader
        },
        mounted() {
            this.ParamsSubProgram[0].Options = this.SubProgramOptions;
            this.ParamsSubProgramStartEnd[0].Options = this.SubProgramOptions;
            this.ParamsSubProgramStartEndReportType[0].Options = this.SubProgramOptions;
            this.ParamsSubProgramYear[0].Options = this.SubProgramOptions;
            this.ParamsSubProgram[0].Options = this.SubProgramOptions;
            this.ParamsSubProgramStatus[0].Options = [{ text: "All Programs", value: "00000000-0000-0000-0000-000000000000" }, ...this.SubProgramOptions];
            this.ParamsSubProgramEffectiveDate[0].Options = this.SubProgramOptions;
            this.ParamsSubProgramStartEndProgramOptional[0].Options = this.SubProgramOptions;
            this.ParamsSubProgramCaseloadOptions[0].Options = this.SubProgramOptions;
            this.ParamsSubProgramMonthYearMode[0].Options = this.SubProgramOptions;
            this.ParamsSubProgramMonthYear[0].Options = this.SubProgramOptions;
            this.ParamsClientBirthdayOptions[0].Options = this.SubProgramOptions;
            this.ParamsSubProgramMultiSelect[0].Options = this.SubProgramOptions;
            this.ParamsSubProgramRegion[0].Options = this.SubProgramRegionOptions;
            this.ParamsSubProgramMultiSelectStartEnd[0].Options = this.SubProgramOptions;
            this.ParamsPNWCaregiverSupportImportClients[0].Options = this.SubProgramOptions.filter(x => x.text.includes('Caregiver Support'));
        }
    }
</script>

<style scoped>

</style>